import React from 'react';
import { Text } from '@mantine/core';
import dayjs from 'dayjs';
import { DataTableColumn } from 'mantine-datatable';

import { Request } from '../../../codegen/graphql';
import { PRICING_BATCH_INNER_TABLE_COLUMN_WIDTH_PX } from '../../../constants/pricingBatchTableColumnWidths';
import { DEFAULT_APP_VAT_PERCENTAGE } from '../../../constants/appConfigDefaults';
import { SimpleHeader } from '../components/TableHeaderWrapper';
import {
    formatPercent,
    renderPriceDirection,
    renderSku,
    renderStoreDetails,
} from '../requestsTableRenderers';
import { ProductSalesTableCell } from '../table-cell-detail/ProductSalesTableCell';
import { RequestHistoryTableCell } from '../table-cell-detail/RequestHistoryTableCell';
import { FuturePricesTableCell } from '../table-cell-detail/FuturePricesTableCell';
import type { ColumnBuilderCtx } from '../BaseRequestsTable';
import { PricingBatchActionsCell } from '../pricing-batch-actions-cell/PricingBatchActionsCell';
import { PriceChangeScopeBadge } from '../PriceChangeScopeBadge';
import {
    PricingBatchCostPriceCell,
    PricingBatchCurrencyAmountCell,
    PricingBatchCurrentMarginCell,
    PricingBatchInterimPriceCell,
    PricingBatchProposedMarginCell,
    PricingBatchProposedPriceCell,
    PRICING_BATCH_TABLE_VALUE_CLASS,
} from '../PricingBatchIndicatorCells';

const RowNumberHeader = <SimpleHeader text="#" />;
const PriceDirectionHeader = <SimpleHeader text="Increase / Decrease" />;
const DateRequestedHeader = <SimpleHeader text="Date Requested" />;
const ResponseDateHeader = <SimpleHeader text="Response Date" />;
const CompetitorPriceHeader = <SimpleHeader text="Competitor Price" />;
const ProposedPriceHeader = <SimpleHeader text="Proposed Price" />;
const InterimPriceHeader = <SimpleHeader text="Interim Price" />;
const CostPriceHeader = <SimpleHeader text="Cost Price" />;
const CurrentPriceHeader = <SimpleHeader text="Current Price" />;
const CurrentMarginHeader = <SimpleHeader text="Current Margin" />;
const ProposedMarginHeader = <SimpleHeader text="Proposed Margin" />;
const BenchmarkMarginHeader = <SimpleHeader text="Benchmark Margin" />;
const ProductSalesHeader = <SimpleHeader text="Product Sales" />;
const RequestHistoryHeader = <SimpleHeader text="Request History" />;
const FuturePricesHeader = <SimpleHeader text="Future prices" />;
const ActionsHeader = <SimpleHeader text="Actions" />;

export type PricingBatchesColumnsOptions = {
    hideColumns?: string[];
    rowIndexOffset?: number;
    uniformColumnWidths?: boolean;
};

type InnerWidthKey = keyof typeof PRICING_BATCH_INNER_TABLE_COLUMN_WIDTH_PX;

export function getPricingBatchesColumns(
    ctx: ColumnBuilderCtx,
    options?: PricingBatchesColumnsOptions,
): DataTableColumn<Request>[] {
    const { headers } = ctx;
    const hidden = new Set(options?.hideColumns ?? []);
    const rowIndexOffset = options?.rowIndexOffset ?? 0;
    const vatPct =
        ctx.pricingBatchIndicators?.vatPercentage ?? DEFAULT_APP_VAT_PERCENTAGE;
    const hideCostAndMargins =
        ctx.pricingBatchIndicators?.hideCostAndMargins ?? false;
    const uniformWidths = Boolean(options?.uniformColumnWidths);
    const W = PRICING_BATCH_INNER_TABLE_COLUMN_WIDTH_PX;

    const uw = (
        key: InnerWidthKey,
    ):
        | Pick<DataTableColumn<Request>, 'width' | 'cellsClassName'>
        | Record<string, never> => {
        if (!uniformWidths) {
            return {};
        }
        return {
            width: W[key],
            cellsClassName: 'min-w-0 align-middle',
        };
    };

    const columns: DataTableColumn<Request>[] = [
        {
            accessor: 'rowNumber',
            title: RowNumberHeader,
            textAlign: 'center',
            noWrap: true,
            render: (_record, index) => rowIndexOffset + (index ?? 0) + 1,
            ...uw('rowNumber'),
        },
        {
            accessor: 'sku',
            title: headers.Sku,
            render: renderSku,
            textAlign: 'left',
            noWrap: true,
            sortable: true,
            ...uw('sku'),
        },
        {
            accessor: 'productCategory',
            title: headers.ProductCategory,
            render: ({ productCategory }) =>
                productCategory != null && productCategory.trim() !== '' ? (
                    <Text
                        size="sm"
                        className="truncate"
                        title={productCategory}
                    >
                        {productCategory}
                    </Text>
                ) : (
                    'N/A'
                ),
            textAlign: 'left',
            noWrap: true,
            sortable: true,
            ...uw('productCategory'),
        },
        {
            accessor: 'store',
            title: headers.Store,
            render: renderStoreDetails,
            textAlign: 'left',
            noWrap: true,
            sortable: true,
            ...uw('store'),
        },
        {
            accessor: 'priceChangeScope',
            title: headers.Scope ?? <SimpleHeader text="Scope" />,
            render: request => (
                <PriceChangeScopeBadge request={request} size="sm" />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('priceChangeScope'),
        },
        {
            accessor: 'competitorPrice',
            title: CompetitorPriceHeader,
            render: request => (
                <PricingBatchCurrencyAmountCell
                    request={request}
                    amount={request.competitorPrice ?? 0}
                />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('competitorPrice'),
        },
        {
            accessor: 'proposedPrice',
            title: ProposedPriceHeader,
            render: request => (
                <PricingBatchProposedPriceCell
                    request={request}
                    vatPercentage={
                        (request as { resolvedVatPercentage?: number | null })
                            .resolvedVatPercentage ?? vatPct
                    }
                    hideCostAndMargins={hideCostAndMargins}
                />
            ),
            textAlign: 'left',
            noWrap: true,
            sortable: true,
            ...uw('proposedPrice'),
        },
        {
            accessor: 'interimPrice',
            title: InterimPriceHeader,
            render: (request: Request) => (
                <PricingBatchInterimPriceCell request={request} />
            ),
            textAlign: 'left',
            noWrap: true,
            sortable: false,
            ...uw('interimPrice'),
        },
        {
            accessor: 'costPrice',
            title: CostPriceHeader,
            render: request => (
                <PricingBatchCostPriceCell
                    request={request}
                    hideCostAndMargins={hideCostAndMargins}
                />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('costPrice'),
        },
        {
            accessor: 'currentPrice',
            title: CurrentPriceHeader,
            render: request => (
                <PricingBatchCurrencyAmountCell
                    request={request}
                    amount={request.currentPrice ?? 0}
                />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('currentPrice'),
        },
        {
            accessor: 'currentMargin',
            title: CurrentMarginHeader,
            render: request => (
                <PricingBatchCurrentMarginCell request={request} />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('currentMargin'),
        },
        {
            accessor: 'proposedMargin',
            title: ProposedMarginHeader,
            render: request => (
                <PricingBatchProposedMarginCell
                    request={request}
                    hideCostAndMargins={hideCostAndMargins}
                />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('proposedMargin'),
        },
        {
            accessor: 'benchmarkMargin',
            title: BenchmarkMarginHeader,
            render: ({ benchmarkMargin }) => (
                <div className="flex h-6 min-w-0 items-center">
                    <Text size="sm" className={PRICING_BATCH_TABLE_VALUE_CLASS}>
                        {formatPercent(benchmarkMargin)}
                    </Text>
                </div>
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('benchmarkMargin'),
        },
        {
            accessor: 'requestHistoryLabel',
            title: RequestHistoryHeader,
            render: (request: Request) => (
                <RequestHistoryTableCell request={request} />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('requestHistoryLabel'),
        },
        {
            accessor: 'futurePrices',
            title: FuturePricesHeader,
            render: (request: Request) => (
                <FuturePricesTableCell request={request} />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('futurePrices'),
        },
        {
            accessor: 'productSales',
            title: ProductSalesHeader,
            render: (request: Request) => (
                <ProductSalesTableCell request={request} />
            ),
            textAlign: 'left',
            noWrap: true,
            ...uw('productSales'),
        },
        {
            accessor: 'dateRequested',
            title: DateRequestedHeader,
            render: ({ createdAt }) =>
                createdAt ? dayjs(createdAt).format('DD MMM YYYY HH:mm') : '—',
            textAlign: 'left',
            noWrap: true,
            sortable: true,
            ...uw('dateRequested'),
        },
        {
            accessor: 'responseDate',
            title: ResponseDateHeader,
            render: ({ dueBy }) =>
                dueBy ? dayjs(dueBy).format('DD MMM YYYY HH:mm') : '—',
            textAlign: 'left',
            noWrap: true,
            ...uw('responseDate'),
        },
        {
            accessor: 'friendlyId',
            title: headers.FriendlyId,
            textAlign: 'center',
            noWrap: true,
            sortable: true,
            ...uw('friendlyId'),
        },
        {
            accessor: 'advertisingZone',
            title: headers.AdvertisingZone,
            render: ({ advertisingZoneName }) =>
                advertisingZoneName != null && advertisingZoneName !== '' ? (
                    <Text
                        size="sm"
                        className="truncate"
                        title={advertisingZoneName}
                    >
                        {advertisingZoneName}
                    </Text>
                ) : (
                    'N/A'
                ),
            noWrap: true,
            sortable: true,
            textAlign: 'left',
            ...uw('advertisingZone'),
        },
        {
            accessor: 'priceDirection',
            title: PriceDirectionHeader,
            render: renderPriceDirection,
            textAlign: 'left',
            noWrap: true,
            ...uw('priceDirection'),
        },
        {
            accessor: 'actions',
            title: ActionsHeader,
            textAlign: 'center',
            noWrap: true,
            titleClassName: `!sticky !right-0 !bg-[#f9fafb] !z-[110] whitespace-nowrap shadow-left${
                uniformWidths ? ' min-w-0' : ' w-max'
            }`,
            cellsClassName: `!sticky !right-0 !bg-white !z-[20] whitespace-nowrap shadow-left [tr.bg-blue-50_&]:!bg-blue-50${
                uniformWidths ? ' min-w-0 align-middle' : ' w-max'
            }`,
            ...(uniformWidths ? { width: W.actions } : {}),
            render: (record: Request) => (
                <PricingBatchActionsCell record={record} />
            ),
        },
    ];
    return columns.filter(col => !hidden.has(col.accessor as string));
}
